/**
 * Formulário da vacina do catálogo (#530). O esquema é uma lista de meses contados a partir da
 * 1ª dose — o número de doses é o tamanho dela, não um campo à parte que possa discordar.
 */
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import type { VaccineType, VaccineTypePayload, VaccineTypeStatus } from '../../../types/vaccination';

interface VaccineTypeFormValues {
  name: string;
  status: VaccineTypeStatus;
  description: string;
  scheduleMonths: string;
  boosterMonths: string;
}

/** "0, 1, 6" → [0, 1, 6]; `null` quando algum pedaço não é um número inteiro de meses. */
function lerMeses(texto: string): number[] | null {
  const pedacos = texto.split(/[,;\s]+/).filter((pedaco) => pedaco !== '');
  if (pedacos.length === 0 || !pedacos.every((pedaco) => /^\d+$/.test(pedaco))) return null;
  return pedacos.map(Number);
}

const validationSchema: yup.ObjectSchema<VaccineTypeFormValues> = yup.object({
  name: yup.string().trim().required('O nome da vacina é obrigatório').max(255, 'Máximo de 255 caracteres'),
  status: yup.mixed<VaccineTypeStatus>().oneOf(['ACTIVE', 'INACTIVE']).required(),
  description: yup.string().defined().max(2000, 'Máximo de 2000 caracteres'),
  scheduleMonths: yup
    .string()
    .required('Informe os meses das doses do esquema')
    .test('meses', 'Use números inteiros de meses, separados por vírgula', (valor) => lerMeses(valor) !== null)
    .test('primeira', 'A 1ª dose é o mês 0', (valor) => lerMeses(valor)?.[0] === 0)
    .test('crescente', 'Cada dose vem depois da anterior: os meses devem crescer', (valor) => {
      const meses = lerMeses(valor) ?? [];
      return meses.every((mes, i) => i === 0 || mes > meses[i - 1]);
    }),
  boosterMonths: yup
    .string()
    .defined()
    .test('reforco', 'Reforço é um número inteiro de meses, a partir de 1, ou vazio', (valor) =>
      valor.trim() === '' || (/^\d+$/.test(valor.trim()) && Number(valor) >= 1)
    ),
});

function paraValores(vacina?: VaccineType): VaccineTypeFormValues {
  return {
    name: vacina?.name ?? '',
    status: vacina?.status ?? 'ACTIVE',
    description: vacina?.description ?? '',
    scheduleMonths: vacina ? vacina.scheduleMonths.join(', ') : '0',
    boosterMonths: vacina?.boosterMonths ? String(vacina.boosterMonths) : '',
  };
}

function paraPayload(valores: VaccineTypeFormValues): VaccineTypePayload {
  return {
    name: valores.name.trim(),
    status: valores.status,
    description: valores.description.trim() === '' ? null : valores.description.trim(),
    scheduleMonths: lerMeses(valores.scheduleMonths) ?? [0],
    boosterMonths: valores.boosterMonths.trim() === '' ? null : Number(valores.boosterMonths),
  };
}

interface VaccineTypeFormProps {
  vacina?: VaccineType;
  onSubmit: (data: VaccineTypePayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Recusa do servidor — a trava do esquema, o nome repetido. Fica junto do botão de salvar. */
  erro?: string | null;
}

export const VaccineTypeForm: React.FC<VaccineTypeFormProps> = ({
  vacina,
  onSubmit,
  onCancel,
  isSubmitting = false,
  erro,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VaccineTypeFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: paraValores(vacina),
  });

  return (
    <Box component="form" onSubmit={handleSubmit((valores) => onSubmit(paraPayload(valores)))} noValidate>
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Identificação
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome da vacina"
                  fullWidth
                  required
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message || 'Ex.: Hepatite B'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth required>
              <InputLabel id="vaccine-type-status">Situação</InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select {...field} labelId="vaccine-type-status" label="Situação" disabled={isSubmitting}>
                    <MenuItem value="ACTIVE">Ativa</MenuItem>
                    <MenuItem value="INACTIVE">Inativa</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição"
                  fullWidth
                  multiline
                  rows={2}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Esquema e reforço
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="scheduleMonths"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Meses das doses do esquema"
                  fullWidth
                  required
                  error={Boolean(errors.scheduleMonths)}
                  helperText={
                    errors.scheduleMonths?.message ||
                    'Contados a partir da 1ª dose, separados por vírgula. Ex.: 0, 1, 6 = três doses; 0 = dose única.'
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="boosterMonths"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reforço (meses)"
                  fullWidth
                  type="number"
                  error={Boolean(errors.boosterMonths)}
                  helperText={errors.boosterMonths?.message || 'Depois do esquema completo. Vazio = sem reforço.'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button type="button" onClick={onCancel} variant="outlined" startIcon={<CancelIcon />} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {vacina ? 'Salvar vacina' : 'Criar vacina'}
        </Button>
      </Box>
    </Box>
  );
};

export default VaccineTypeForm;
